/// <amd-module name="Test/TestPlansHubRefresh/Pivots/ExecutePivot/Redux/PointsFilterHelper" />
import { effects, SagaIterator } from "redux-saga";
import {
    IOutcome,
    ITestPointConfiguration,
    ITestPointFilterValues,
    ITestPointItem
} from "Test/TestPlansHubRefresh/Pivots/ExecutePivot/Redux/PointsContracts";
import { TelemetryConstants, TestHubRefreshConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { SuitesActions } from "Test/TestPlansHubRefresh/TestHub/Redux/Suites/SuitesActions";
import { TelemetryActions } from "Test/TestPlansHubRefresh/TestHub/Redux/TelemetryActions";
import { IUserIdentity } from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshContracts";
import { isAdvancedLicenseEnabled } from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshSelector";
import * as Contracts from "TestPlanCommon/Contracts/TestPlan";
import { getCurrentFilterState } from "VSS/Redux/Modules/ContentViewObserver";
import { IFilterItemState, IFilterState } from "VSSUI/Utilities/Filter";

// function to fetch point counts for suite tree when filter is set
export function* fetchFilteredSuitePointCounts(planId: number): SagaIterator {
    const currentFilterState: IFilterState = (yield effects.select(getCurrentFilterState)) as any;
    const filterValuesForCount: ITestPointFilterValues = getFilterValuesForCount(currentFilterState);
    yield effects.put(
        SuitesActions.getSuitePointCount(
            planId,
            filterValuesForCount.states,
            filterValuesForCount.configurations,
            filterValuesForCount.testers,
            filterValuesForCount.assignedTo,
            filterValuesForCount.outcome
        )
    );
}

// function to filter testPoints in our state wrt filterState. (Called when filter changed or we receive a new payload of testpoints)
export function* filterTestPoints(allTestPoints: ITestPointItem[], planId: number) {
    let filteredTestPointsIds: number[] | undefined = undefined;
    // @ts-expect-error TypeScript 4.2 upgrade
    const currentFilterState: IFilterState = (yield effects.select(getCurrentFilterState)) as any;

    if (!allTestPoints) {
        return filteredTestPointsIds;
    }

    filteredTestPointsIds = [];

    for (const testPointItem of allTestPoints) {
        const doesMatchTextFilter = matchesTextFilter(testPointItem, currentFilterState);
        const doesMatchDropdownFilter = matchesDropdownFilter(testPointItem, currentFilterState);
        if (doesMatchTextFilter && doesMatchDropdownFilter) {
            filteredTestPointsIds.push(testPointItem.id);
        }
    }

    const telemetryPayload = {
        pivot: TestHubRefreshConstants.EXECUTE_TAB_ID,
        resultsCount: filteredTestPointsIds.length,
        planId: planId,
        // @ts-expect-error TypeScript 4.2 upgrade
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled),
        textFilterHasValue: filterItemHasValue(currentFilterState[TestHubRefreshConstants.EXECUTE_TEXT_FILTER_ITEM_KEY]),
        outcomeFilterHasValue: filterItemHasValue(currentFilterState[TestHubRefreshConstants.EXECUTE_OUTCOME_FILTER_ITEM_KEY]),
        testerFilterHasValue: filterItemHasValue(currentFilterState[TestHubRefreshConstants.EXECUTE_TESTER_FILTER_ITEM_KEY]),
        configurationFilterHasValue: filterItemHasValue(currentFilterState[TestHubRefreshConstants.EXECUTE_CONFIGURATION_FILTER_ITEM_KEY])
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.FILTER_VALUES_CHANGED, telemetryPayload));
    return filteredTestPointsIds;
}

function matchesTextFilter(testPoint: ITestPointItem, currentFilterState: IFilterState): boolean {
    if (currentFilterState) {
        const filterItemState = currentFilterState[TestHubRefreshConstants.EXECUTE_TEXT_FILTER_ITEM_KEY];

        if (filterItemHasValue(filterItemState) && filterItemState!.value.trim().length > 0) {
            const filterValText = filterItemState!.value.trim();
            let filterTextMatchesTitle: boolean = false;
            let filterTextMatchesId: boolean = false;
            let filterTextMatchesTag: boolean = false;

            if (testPoint && testPoint.testCaseRef && filterValText) {
                filterTextMatchesTitle = testPoint.testCaseRef.title.toLocaleLowerCase().indexOf(filterValText.toLocaleLowerCase()) !== -1;
                filterTextMatchesId = testPoint.testCaseRef.id.toString().indexOf(filterValText) !== -1;
                if (testPoint.columnOptionValues && testPoint.columnOptionValues[TestHubRefreshConstants.WorkItemField_Tags]) {
                    const tags = testPoint.columnOptionValues[TestHubRefreshConstants.WorkItemField_Tags].split(" ;");
                    for (const tag of tags) {
                        if (tag.toLocaleLowerCase().indexOf(filterValText.toLocaleLowerCase()) !== -1) {
                            filterTextMatchesTag = true;
                            break;
                        }
                    }
                }
            }

            return filterTextMatchesTitle || filterTextMatchesId || filterTextMatchesTag;
        }
    }

    return true;
}

function matchesDropdownFilter(testPoint: ITestPointItem, currentFilterState: IFilterState): boolean {
    let doesMatchOutcomeFilter = true,
        doesMatchTesterFilter = true,
        doesMatchConfigurationFilter = true,
        doesMatchAssignedToFilter = true,
        doesMatchStateFilter = true;

    if (testPoint && currentFilterState) {
        doesMatchOutcomeFilter = matchesDropdownFilterItem(
            testPoint.outcomeItem.outcomeString,
            currentFilterState[TestHubRefreshConstants.EXECUTE_OUTCOME_FILTER_ITEM_KEY]
        );
        doesMatchTesterFilter = matchesDropdownFilterItem(
            testPoint.tester.displayName,
            currentFilterState[TestHubRefreshConstants.EXECUTE_TESTER_FILTER_ITEM_KEY]
        );
        doesMatchConfigurationFilter = matchesDropdownFilterItem(
            testPoint.configuration.name,
            currentFilterState[TestHubRefreshConstants.EXECUTE_CONFIGURATION_FILTER_ITEM_KEY]
        );
        doesMatchAssignedToFilter = matchesDropdownFilterItem(
            testPoint.testCaseRef.assignedTo.displayName,
            currentFilterState[TestHubRefreshConstants.EXECUTE_ASSIGNEDTO_FILTER_ITEM_KEY]
        );
        doesMatchStateFilter = matchesDropdownFilterItem(
            testPoint.testCaseRef.state,
            currentFilterState[TestHubRefreshConstants.EXECUTE_STATE_FILTER_ITEM_KEY]
        );
    }

    return doesMatchOutcomeFilter && doesMatchTesterFilter && doesMatchConfigurationFilter && doesMatchAssignedToFilter && doesMatchStateFilter;
}

function matchesDropdownFilterItem(textToMatch: string, filterItemState: IFilterItemState | null) {
    if (filterItemHasValue(filterItemState)) {
        for (const filterValItem of filterItemState!.value) {
            if (filterValItem.key === textToMatch) {
                return true;
            }
        }

        return false;
    }

    return true;
}

function getFilterValuesForCount(currentFilterState: IFilterState): ITestPointFilterValues {
    const states: string[] = [];
    const assignedTo: string[] = [];
    let outcomes: Contracts.UserFriendlyTestOutcome | undefined = undefined;
    const testers: string[] = [];
    const configurations: number[] = [];

    // Configuration
    if (filterItemHasValue(currentFilterState[TestHubRefreshConstants.EXECUTE_CONFIGURATION_FILTER_ITEM_KEY])) {
        for (const filterValItem of currentFilterState[TestHubRefreshConstants.EXECUTE_CONFIGURATION_FILTER_ITEM_KEY]!.value) {
            const configuration: ITestPointConfiguration = filterValItem.value;
            configurations.push(configuration.id);
        }
    }

    if (filterItemHasValue(currentFilterState[TestHubRefreshConstants.EXECUTE_TESTER_FILTER_ITEM_KEY])) {
        for (const filterValItem of currentFilterState[TestHubRefreshConstants.EXECUTE_TESTER_FILTER_ITEM_KEY]!.value) {
            const tester: IUserIdentity = filterValItem.value;

            const id = tester ? tester.id : filterValItem.data.value.id;
            testers.push(id);
        }
    }

    if (filterItemHasValue(currentFilterState[TestHubRefreshConstants.EXECUTE_OUTCOME_FILTER_ITEM_KEY])) {
        for (const filterValItem of currentFilterState[TestHubRefreshConstants.EXECUTE_OUTCOME_FILTER_ITEM_KEY]!.value) {
            const outcome: IOutcome = filterValItem.value;
            outcomes = getUserFriendlyOutcome(outcome.outcome);
            break;
        }
    }

    if (filterItemHasValue(currentFilterState[TestHubRefreshConstants.EXECUTE_ASSIGNEDTO_FILTER_ITEM_KEY])) {
        for (const filterValItem of currentFilterState[TestHubRefreshConstants.EXECUTE_ASSIGNEDTO_FILTER_ITEM_KEY]!.value) {
            const assignedToVal: string = filterValItem.value.id;
            assignedTo.push(assignedToVal);
        }
    }

    if (filterItemHasValue(currentFilterState[TestHubRefreshConstants.EXECUTE_STATE_FILTER_ITEM_KEY])) {
        for (const filterValItem of currentFilterState[TestHubRefreshConstants.EXECUTE_STATE_FILTER_ITEM_KEY]!.value) {
            const state: string = filterValItem.value;
            states.push(state);
        }
    }

    return {
        states: states,
        assignedTo: assignedTo,
        outcome: outcomes,
        testers: testers,
        configurations: configurations
    };
}

function getUserFriendlyOutcome(outcome: Contracts.Outcome): Contracts.UserFriendlyTestOutcome {
    let userFriendlyOutcome: Contracts.UserFriendlyTestOutcome = Contracts.UserFriendlyTestOutcome.Ready;

    switch (outcome) {
        case Contracts.Outcome.Passed:
            userFriendlyOutcome = Contracts.UserFriendlyTestOutcome.Passed;
            break;
        case Contracts.Outcome.Blocked:
            userFriendlyOutcome = Contracts.UserFriendlyTestOutcome.Blocked;
            break;
        case Contracts.Outcome.Failed:
            userFriendlyOutcome = Contracts.UserFriendlyTestOutcome.Failed;
            break;
        case Contracts.Outcome.InProgress:
            userFriendlyOutcome = Contracts.UserFriendlyTestOutcome.InProgress;
            break;
        case Contracts.Outcome.NotApplicable:
            userFriendlyOutcome = Contracts.UserFriendlyTestOutcome.NotApplicable;
            break;
        case Contracts.Outcome.Paused:
            userFriendlyOutcome = Contracts.UserFriendlyTestOutcome.Paused;
            break;
        default:
            userFriendlyOutcome = Contracts.UserFriendlyTestOutcome.Ready;
            break;
    }

    return userFriendlyOutcome;
}

function filterItemHasValue(filterItemState: IFilterItemState | null): boolean {
    return filterItemState && filterItemState!.value && filterItemState!.value.length > 0;
}
